import type { ZodType, z } from 'zod';

/** API のエラー。message は画面にそのまま出せる日本語 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * ログインが切れた（401）ことを画面に知らせる（2026-10-12）。ログインの状態を持つところ（スタジオ・アプリ）が受け取り、
 * ログイン画面へ移す。ログイン・ログアウトそのもの（/auth/…）の失敗は知らせない
 */
export const UNAUTHORIZED_EVENT = 'qs:unauthorized';
export type UnauthorizedDetail = { base: string; path: string };

async function request<T>(base: string, method: string, path: string, body?: unknown, schema?: ZodType<T>): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network', '通信できませんでした。インターネットの接続を確かめてください');
  }
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/auth/') && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<UnauthorizedDetail>(UNAUTHORIZED_EVENT, { detail: { base, path } }));
    }
    const e = json as { code?: string; message?: string } | null;
    throw new ApiError(res.status, e?.code ?? 'error', e?.message ?? 'うまくいきませんでした。もう一度お試しください');
  }
  return schema ? schema.parse(json) : (json as T);
}

/** base：ユーザー側は /api、スタジオは /api/studio */
export function createApi(base: string) {
  return {
    get: <T = unknown>(path: string, schema?: ZodType<T>) => request<T>(base, 'GET', path, undefined, schema),
    post: <T = unknown>(path: string, body: unknown = {}, schema?: ZodType<T>) => request<T>(base, 'POST', path, body, schema),
    patch: <T = unknown>(path: string, body: unknown = {}, schema?: ZodType<T>) => request<T>(base, 'PATCH', path, body, schema),
    put: <T = unknown>(path: string, body: unknown = {}, schema?: ZodType<T>) => request<T>(base, 'PUT', path, body, schema),
    del: <T = unknown>(path: string, body: unknown = {}, schema?: ZodType<T>) => request<T>(base, 'DELETE', path, body, schema),
  };
}

export type Api = ReturnType<typeof createApi>;

export const api = createApi('/api');

export type Infer<T extends ZodType> = z.infer<T>;

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return 'うまくいきませんでした。もう一度お試しください';
}
